import { Plus, Search, X } from 'lucide-react'
import { useState, useMemo, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useGetOrgId } from 'src/api/helpers/getOrgId'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { useQuoteItemApi } from 'src/api/db/apiQuoteItem'
import { queryKeys } from 'src/api/db/queryKeys'
import { ensureQuoteItemCanvasStub } from 'src/api/db/utils/ensureQuoteItemCanvasStub'
import { useSupabaseClient } from 'src/api/supabase'
import { Column } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui'
import { Input } from 'src/shared/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { DELIVERED_STATUS } from 'src/shared/consts/quoteStatus'

import { QUOTE_SEARCH_THRESHOLD } from './consts'
import { filterQuotesByName, sortQuotesByCreatedAt } from './utils'

export const AddToOrderButton = ({
  isQuote,
  product_id,
  product_variant_color_id,
  quantity = 1,
  unit_price,
  org_id: orgIdProp,
  className,
  onSuccess,
  addToCurrentQuoteId,
  inOrderQuoteItemId,
  description,
}: {
  isQuote?: boolean
  product_id?: number
  product_variant_color_id?: number | null
  quantity?: number
  unit_price: number
  org_id?: string
  className?: string
  onSuccess?: () => void
  addToCurrentQuoteId?: number
  inOrderQuoteItemId?: number
  description: string
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')

  const isAdmin = useIsAdmin()
  const resolvedOrgId = useGetOrgId()
  const org_id = orgIdProp || resolvedOrgId

  const { quotes, createQuote } = useQuoteApi({
    org_id: isAdmin ? undefined : org_id,
    // List query stays disabled until the menu is open (see useQuoteApi `enabled`).
    skipListQuery: !isOpen,
    list: isAdmin
      ? {
          limit: 50,
          includeProofs: false,
          includeItemThumbnails: false,
          excludeStatuses: [DELIVERED_STATUS],
        }
      : undefined,
  })
  const { createQuoteItem, deleteQuoteItem } = useQuoteItemApi({})

  const isMutationPending =
    createQuote.isPending ||
    createQuoteItem.isPending ||
    deleteQuoteItem.isPending

  const showErrorToast = useCallback(
    (description: string) => {
      toast({ title: t('Error', 'Fel'), description, variant: 'destructive' })
    },
    [toast, t]
  )

  const ensureOrgOrAdmin = useCallback((): boolean => {
    if (!org_id && !isAdmin) {
      showErrorToast(
        t(
          'Please log in to add items to quote',
          'Logga in för att lägga till objekt i order'
        )
      )
      return false
    }
    return true
  }, [org_id, isAdmin, showErrorToast, t])

  const buttonLabel = isQuote
    ? t('Add variant', 'Lägg till variant')
    : t('Add to order', 'Lägg till i order')

  const handleAddToQuote = async (quote_id: number) => {
    if (!ensureOrgOrAdmin()) return

    try {
      const result = await createQuoteItem.mutateAsync({
        quote_id,
        product_id,
        product_variant_color_id,
        description,
        quantity,
        unit_price,
        type: 'product',
      })

      if (addToCurrentQuoteId) {
        const quoteItemId = result.data?.id
        if (quoteItemId) {
          await ensureQuoteItemCanvasStub(supabase, {
            quoteItemId,
            quantity,
            decorationDescription: t('New decoration', 'Ny dekoration'),
          })
          void queryClient.invalidateQueries({
            queryKey: [queryKeys.quoteItems, addToCurrentQuoteId],
          })
        }
      }

      setIsOpen(false)
      onSuccess?.()
    } catch (error) {
      console.error('Error adding product to quote:', error)
      showErrorToast(
        t(
          'Failed to add product to quote',
          'Kunde inte lägga till produkt i order'
        )
      )
    }
  }

  const handleCreateNewQuote = async () => {
    if (!ensureOrgOrAdmin()) return

    try {
      const quotesList = quotes?.data || []
      const nextQuoteNumber = quotesList.length + 1
      const quoteName = `${t('Order', 'Order')} #${nextQuoteNumber}`

      const quoteResult = await createQuote.mutateAsync({
        org_id: org_id!,
        name: quoteName,
      })

      if (!quoteResult?.id) {
        throw new Error('Failed to create quote')
      }

      await handleAddToQuote(quoteResult.id)
    } catch (error) {
      console.error('Error creating new quote:', error)
      showErrorToast(
        t('Failed to create new quote', 'Kunde inte skapa ny order')
      )
    }
  }

  const sortedQuotes = useMemo(
    () => sortQuotesByCreatedAt(quotes?.data ?? []),
    [quotes?.data]
  )

  const showSearch = sortedQuotes.length > QUOTE_SEARCH_THRESHOLD

  const filteredQuotes = useMemo(
    () => filterQuotesByName(sortedQuotes, search),
    [sortedQuotes, search]
  )

  if (addToCurrentQuoteId) {
    if (inOrderQuoteItemId) {
      return (
        <Button
          variant="destructive"
          size="sm"
          className={cn('w-full', className)}
          disabled={isMutationPending}
          onClick={() =>
            deleteQuoteItem.mutate({
              id: inOrderQuoteItemId,
              quote_id: addToCurrentQuoteId,
            })
          }
        >
          <X className="w-4 h-4 mr-2" />
          {t('Remove from order', 'Ta bort från order')}
        </Button>
      )
    }

    return (
      <Button
        size="sm"
        className={cn('w-full', className)}
        disabled={isMutationPending}
        onClick={() => handleAddToQuote(addToCurrentQuoteId)}
      >
        <Plus className="w-4 h-4 mr-2" />
        {buttonLabel}
      </Button>
    )
  }

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) setSearch('')
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          className={cn('w-full', className)}
          disabled={isMutationPending}
        >
          <Plus className="w-4 h-4 mr-2" />
          {buttonLabel}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs font-medium">
          {isQuote
            ? t('Add variant to', 'Lägg till variant i')
            : t('Add product to', 'Lägg till produkt i')}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {showSearch && (
          <Column className="px-2 pb-1.5">
            <Input
              placeholder={t('Search orders...', 'Sök ordrar...')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              startContent={
                <Search className="w-3.5 h-3.5 text-muted-foreground" />
              }
              className="h-8"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Column>
        )}

        {filteredQuotes.length > 0 ? (
          <Column className="max-h-[200px] overflow-y-auto">
            {filteredQuotes.map((quote, index) => (
              <DropdownMenuItem
                key={quote.id}
                onClick={() => handleAddToQuote(quote.id)}
                className="cursor-pointer"
              >
                {quote.name || `${t('Order', 'Order')} ${index + 1}`}
              </DropdownMenuItem>
            ))}
          </Column>
        ) : (
          <DropdownMenuItem disabled className="text-muted-foreground">
            {search
              ? t('No matching orders', 'Inga matchande ordrar')
              : t('No orders created', 'Inga ordrar skapade')}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Button
            variant={sortedQuotes.length > 0 ? 'secondary' : 'primary'}
            size="sm"
            className="w-full font-medium"
            onClick={handleCreateNewQuote}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('New order', 'Ny order')}
          </Button>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
